import { Control, FieldErrors, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SsccConfiguration } from 'services/repositories/sscc/sscc';
import { Section } from 'components/Display/Section/Section';
import { Dropdown } from 'components/Form/Dropdown/Dropdown';

import { SsccSettingsFormState } from '../helpers';

/**
 * The shared Dropdown hands `value ?? ''` to Headless UI, so a `null` option is never marked selected. The
 * not-set choice uses `''` in the select instead, and maps back to the `null` the API expects.
 */
const NOT_SET = '';

type RequirementSectionProps = {
  control: Control<SsccSettingsFormState>;
  unitOfMeasureOptions: NonNullable<SsccConfiguration['requirement']>['unit_of_measure_options'];
  errors: FieldErrors<SsccSettingsFormState>['requirement'];
  disabled: boolean;
};

export const RequirementSection = ({ control, unitOfMeasureOptions, errors, disabled }: RequirementSectionProps) => {
  const { t } = useTranslation();
  const { field } = useController({ name: 'requirement.shipping_unit_of_measure', control });

  return (
    <Section.Horizontal
      title={t('settings:ssccSettings.requirement.title')}
      description={t('settings:ssccSettings.requirement.description')}
    >
      <Dropdown<string>
        label={t('settings:ssccSettings.form.shippingUnitOfMeasure')}
        description={t('settings:ssccSettings.form.shippingUnitOfMeasureSubtitle')}
        options={[
          {
            value: NOT_SET,
            label: t('settings:ssccSettings.form.shippingUnitOfMeasureNotSet'),
            testId: 'unitOfMeasure-notSet-option',
          },
          ...unitOfMeasureOptions.map((option) => ({ ...option, testId: `unitOfMeasure-${option.value}-option` })),
        ]}
        value={field.value ?? NOT_SET}
        onChange={(value) => field.onChange(value === NOT_SET ? null : value)}
        errors={errors?.shipping_unit_of_measure}
        disabled={disabled}
        dropdownDisplayTestId="shippingUnitOfMeasureDropdown"
      />
    </Section.Horizontal>
  );
};
